/**
 * LROChart - LRO oscillator with thresholds and diamond markers
 */

import { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import './LROChart.css';

export default function LROChart({ data, height = 150, onVisibleRangeChange }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const lroSeriesRef = useRef(null);
  const [lroValue, setLroValue] = useState(null);

  useEffect(() => {
    if (!chartContainerRef.current || !data || !data.lro) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { color: '#1a1a1a' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#2b2b2b' },
        horzLines: { color: '#2b2b2b' },
      },
      crosshair: {
        mode: 1,
      },
      timeScale: {
        borderColor: '#2b2b2b',
        visible: false,
      },
      leftPriceScale: {
        borderColor: '#2b2b2b',
        autoScale: true,
        visible: true,
        scaleMargins: {
          top: 0.15,
          bottom: 0.15,
        },
      },
      rightPriceScale: {
        visible: false,
      },
    });

    chartRef.current = chart;

    // Add LRO area series
    if (data.lro.values && data.lro.values.length > 0) {
      const lroSeries = chart.addAreaSeries({
        priceScaleId: 'left',
        topColor: 'rgba(33, 150, 243, 0.4)',
        bottomColor: 'rgba(33, 150, 243, 0.0)',
        lineColor: '#2196F3',
        lineWidth: 2,
      });
      lroSeriesRef.current = lroSeries;

      const lroData = data.lro.values.map(d => ({
        time: d.time,
        value: d.value,
      }));

      lroSeries.setData(lroData);

      // Add diamond markers for threshold crossings
      const markers = [];
      for (let i = 1; i < data.lro.values.length; i++) {
        const prev = data.lro.values[i - 1];
        const curr = data.lro.values[i];

        // Bullish signal: LRO crosses above buy threshold (1.0)
        if (prev.value <= 1.0 && curr.value > 1.0) {
          markers.push({
            time: curr.time,
            position: 'belowBar',
            color: '#4caf50',
            shape: 'arrowUp',
            text: 'Buy',
          });
        }

        // Bearish signal: LRO crosses below sell threshold (-1.0)
        if (prev.value >= -1.0 && curr.value < -1.0) {
          markers.push({
            time: curr.time,
            position: 'aboveBar',
            color: '#f44336',
            shape: 'arrowDown',
            text: 'Sell',
          });
        }
      }

      if (markers.length > 0) {
        lroSeries.setMarkers(markers);
      }
    }

    // Add threshold lines
    const addThresholdLine = (value, color, label) => {
      const thresholdSeries = chart.addLineSeries({
        priceScaleId: 'left',
        color,
        lineWidth: 1,
        lineStyle: 2, // Dashed
        title: label,
      });

      const thresholdData = data.lro.values.map(d => ({
        time: d.time,
        value: value,
      }));

      thresholdSeries.setData(thresholdData);
    };

    // Only add threshold lines if LRO data exists
    if (data.lro.values && data.lro.values.length > 0) {
      addThresholdLine(1.0, '#4caf50', 'Buy Threshold');
      addThresholdLine(-1.0, '#f44336', 'Sell Threshold');
      addThresholdLine(0, '#757575', 'Zero Line');
    }

    // Subscribe to crosshair movement for value display
    chart.subscribeCrosshairMove((param) => {
      if (param.time && param.seriesData && param.seriesData.size > 0) {
        param.seriesData.forEach((data) => {
          if (data && typeof data.value !== 'undefined' && !data.color) {
            setLroValue(data.value);
          }
        });
      } else if (data.lro.values && data.lro.values.length > 0) {
        const latest = data.lro.values[data.lro.values.length - 1];
        setLroValue(latest?.value);
      }

      // Sync crosshair across all charts
      if (window.chartSync && chart) {
        window.chartSync.syncCrosshair(chart, param);
      }
    });

    // Initialize with latest value
    if (data.lro.values && data.lro.values.length > 0) {
      const latest = data.lro.values[data.lro.values.length - 1];
      setLroValue(latest?.value);
    }

    if (onVisibleRangeChange) {
      chart.timeScale().subscribeVisibleTimeRangeChange(onVisibleRangeChange);
    }

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [data, height, onVisibleRangeChange]);

  useEffect(() => {
    if (chartRef.current && lroSeriesRef.current && window.chartSync) {
      window.chartSync.registerChart('lro', chartRef.current, lroSeriesRef.current);
    }
  }, [chartRef.current, lroSeriesRef.current]);

  return (
    <div className="lro-chart">
      <div className="chart-value-display">
        {lroValue !== null && (
          <span className="lro-value">
            LRO: {lroValue.toFixed(4)}
          </span>
        )}
      </div>
      <div ref={chartContainerRef} className="chart-container" />
    </div>
  );
}
